/** P32 (Feb 27, 2026) — Right pane form: AI hints, patient/employee
 *  selects, visit-entries editor, signatures. */
import React from 'react';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';

import { entryUnits, readinessBadgeTone } from './utils';

const ReviewFormPane = ({
  ts, patients, employees,
  patientId, setPatientId, employeeId, setEmployeeId,
  entries, updateEntry, totalUnits,
  aideSig, setAideSig, supSig, setSupSig,
  onFieldFocus,
}) => (
  <div className="flex-1 overflow-y-auto p-5 space-y-4">
    {/* AI hints */}
    <div className="bg-indigo-50 border border-indigo-100 rounded-md p-3 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-indigo-900">AI extracted</span>
        {ts.readiness && (
          <Badge className={`text-[10px] ${readinessBadgeTone(ts.readiness.level)}`}>
            {ts.readiness.confidence}% · {ts.readiness.level}
          </Badge>
        )}
      </div>
      <div className="text-indigo-800">
        Patient: <span className="italic">{ts.extracted_data?.patient_name || '—'}</span>
      </div>
      <div className="text-indigo-800">
        Staff: <span className="italic">{ts.extracted_data?.staff_name || '—'}</span>
      </div>
    </div>

    {/* Patient */}
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1 block">
        Patient {ts.patient_match && (
          <span className="ml-1 text-emerald-600 font-normal">
            AI {ts.patient_match.confidence}%
          </span>
        )}
      </label>
      <Select value={patientId} onValueChange={setPatientId}>
        <SelectTrigger
          data-testid="review-patient-select"
          onFocus={() => onFieldFocus?.('patient_name')}
        >
          <SelectValue placeholder="Select patient…" />
        </SelectTrigger>
        <SelectContent>
          {patients.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.first_name} {p.last_name}
              {p.medicaid_id_last4 ? ` (•••${p.medicaid_id_last4})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Employee */}
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1 block">
        Employee {ts.employee_match && (
          <span className="ml-1 text-emerald-600 font-normal">
            AI {ts.employee_match.confidence}%
          </span>
        )}
      </label>
      <Select value={employeeId} onValueChange={setEmployeeId}>
        <SelectTrigger
          data-testid="review-employee-select"
          onFocus={() => onFieldFocus?.('staff_name')}
        >
          <SelectValue placeholder="Select employee…" />
        </SelectTrigger>
        <SelectContent>
          {employees.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              {e.first_name} {e.last_name}
              {e.employee_id ? ` (${e.employee_id})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Visits */}
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          Visits ({entries.length})
        </label>
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-indigo-900">{totalUnits} units</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {entries.map((entry) => (
          <div
            key={entry._idx}
            className="grid grid-cols-12 gap-1.5 items-center"
          >
            <Input
              value={entry.date || ''}
              onChange={(e) => updateEntry(entry._idx, 'date', e.target.value)}
              onFocus={() => onFieldFocus?.(`entry-${entry._idx}`)}
              className="h-8 text-xs col-span-4"
              placeholder="MM/DD/YYYY"
              data-testid={`review-date-${entry._idx}`}
            />
            <Input
              value={entry.time_in || ''}
              onChange={(e) => updateEntry(entry._idx, 'time_in', e.target.value)}
              onFocus={() => onFieldFocus?.(`entry-${entry._idx}`)}
              className="h-8 text-xs col-span-3"
              placeholder="9:00 AM"
              data-testid={`review-timein-${entry._idx}`}
            />
            <Input
              value={entry.time_out || ''}
              onChange={(e) => updateEntry(entry._idx, 'time_out', e.target.value)}
              onFocus={() => onFieldFocus?.(`entry-${entry._idx}`)}
              className="h-8 text-xs col-span-3"
              placeholder="5:00 PM"
              data-testid={`review-timeout-${entry._idx}`}
            />
            <div className="col-span-2 text-right text-xs font-semibold text-indigo-900">
              {entryUnits(entry)}u
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Signatures */}
    <div className="flex gap-4 pt-2 border-t border-slate-100">
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <Checkbox checked={aideSig} onCheckedChange={setAideSig} data-testid="review-aide-sig" />
        <span>Aide sig</span>
      </label>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <Checkbox checked={supSig} onCheckedChange={setSupSig} data-testid="review-sup-sig" />
        <span>Supervisor sig</span>
      </label>
    </div>
  </div>
);

export default ReviewFormPane;
